import { FC, useState, useEffect } from "react";
import { Box, Text, Flex, Portal } from "@chakra-ui/react";

interface ClippyProps {
  isVisible?: boolean;
}

const clippyMessages = [
  "👋 Hi! I'm Clippy! It looks like you're viewing a resume. Would you like help?",
  "💡 Tip: Click on the window buttons to minimize or maximize!",
  "📝 Don't forget to sign the guestbook!",
  "🚀 Yosri is available for hire! Just saying...",
  "☕ Yosri has consumed approximately 10,000 cups of coffee.",
  "🎮 Did you know? This site has easter eggs! Try clicking around!",
  "💼 Pro tip: Yosri knows React, Node.js, and many more!",
  "🌟 Fun fact: This retro design was made with love!",
  "📧 Psst... you should totally contact Yosri!",
  "🔥 This resume is fire! No, literally, look at the text effects!",
];

export const Clippy: FC<ClippyProps> = ({ isVisible = true }) => {
  const [isShowing, setIsShowing] = useState(false);
  const [message, setMessage] = useState("");
  const [isDismissed, setIsDismissed] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Check if user has dismissed clippy before
    const dismissed = localStorage.getItem("clippy_dismissed");
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Show clippy after a delay
    const showTimer = setTimeout(() => {
      if (!isDismissed) {
        setMessage(clippyMessages[0]);
        setIsShowing(true);
      }
    }, 3000);

    // Cycle through messages
    const messageInterval = setInterval(() => {
      if (!isDismissed) {
        const randomMessage =
          clippyMessages[Math.floor(Math.random() * clippyMessages.length)];
        setMessage(randomMessage);
      }
    }, 15000);

    return () => {
      clearTimeout(showTimer);
      clearInterval(messageInterval);
    };
  }, [isDismissed]);

  // Random position on screen edge
  useEffect(() => {
    const updatePosition = () => {
      setPosition({
        x: window.innerWidth - 200,
        y: window.innerHeight - 250,
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  const handleDismiss = () => {
    setIsShowing(false);
  };

  const handleNeverShowAgain = () => {
    localStorage.setItem("clippy_dismissed", "true");
    setIsDismissed(true);
    setIsShowing(false);
  };

  const handleShowAgain = () => {
    localStorage.removeItem("clippy_dismissed");
    setIsDismissed(false);
    setMessage(clippyMessages[0]);
    setIsShowing(true);
  };

  if (!isVisible || isDismissed) {
    // Show a small button to bring clippy back
    return (
      <Box position="fixed" bottom="10px" right="10px" zIndex={999}>
        <Box
          as="button"
          bg="#FFFF00"
          border="2px outset white"
          px={2}
          py={1}
          fontSize="xs"
          fontFamily="'Comic Sans MS', cursive"
          cursor="pointer"
          _hover={{ bg: "#FFCC00" }}
          onClick={handleShowAgain}
        >
          📎 Clippy
        </Box>
      </Box>
    );
  }

  if (!isShowing) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        left={`${position.x}px`}
        top={`${position.y}px`}
        zIndex={1500}
        animation="clippyBounce 0.5s ease-out"
        sx={{
          "@keyframes clippyBounce": {
            "0%": { transform: "scale(0) rotate(-180deg)" },
            "50%": { transform: "scale(1.2) rotate(10deg)" },
            "100%": { transform: "scale(1) rotate(0deg)" },
          },
        }}
      >
        {/* Speech Bubble */}
        <Box
          bg="#FFFFCC"
          border="2px solid #000"
          borderRadius="10px"
          p={3}
          maxW="200px"
          position="relative"
          mb={2}
          boxShadow="2px 2px 0px #808080"
        >
          <Text
            fontFamily="'Comic Sans MS', cursive"
            fontSize="sm"
            color="black"
          >
            {message}
          </Text>
          {/* Bubble tail */}
          <Box
            position="absolute"
            bottom="-10px"
            left="30px"
            w="0"
            h="0"
            borderLeft="10px solid transparent"
            borderRight="10px solid transparent"
            borderTop="10px solid #FFFFCC"
          />
          <Box
            position="absolute"
            bottom="-12px"
            left="29px"
            w="0"
            h="0"
            borderLeft="11px solid transparent"
            borderRight="11px solid transparent"
            borderTop="11px solid #000"
            zIndex={-1}
          />
        </Box>

        {/* Clippy Character */}
        <Flex direction="column" align="center">
          <Text
            fontSize="5xl"
            cursor="pointer"
            onClick={() => {
              const randomMessage =
                clippyMessages[
                  Math.floor(Math.random() * clippyMessages.length)
                ];
              setMessage(randomMessage);
            }}
          >
            📎
          </Text>

          {/* Action Buttons */}
          <Flex gap={1} mt={1}>
            <Box
              as="button"
              bg="#C0C0C0"
              border="1px outset white"
              px={2}
              py={0.5}
              fontSize="xs"
              fontFamily="'Comic Sans MS', cursive"
              cursor="pointer"
              _hover={{ bg: "#A0A0A0" }}
              onClick={handleDismiss}
            >
              Hide
            </Box>
            <Box
              as="button"
              bg="#FF6666"
              border="1px outset #FF8888"
              px={2}
              py={0.5}
              fontSize="xs"
              fontFamily="'Comic Sans MS', cursive"
              color="white"
              cursor="pointer"
              _hover={{ bg: "#CC0000" }}
              onClick={handleNeverShowAgain}
            >
              Go Away
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Portal>
  );
};
